import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import MarkdownToolbar from "@/components/MarkdownToolbar";
import AlignmentToggle from "@/components/AlignmentToggle";
import { POLL_COLOR_PALETTE, paletteColor } from "@/lib/pollColors";
import { toast } from "sonner";

export default function EditPostModal({ post, onClose, onSaved }) {
  const [title, setTitle] = useState(post.title || "");
  const [topic, setTopic] = useState(post.topic || "General");
  const [summary, setSummary] = useState(post.summary || "");
  const [body, setBody] = useState(post.body || "");
  const [options, setOptions] = useState(post.poll_options || ["", ""]);
  const [chartType, setChartType] = useState(post.chart_type || "bar");
  const [optionColors, setOptionColors] = useState(() => {
    const existing = Array.isArray(post.chart_colors) ? post.chart_colors : [];
    const count = (post.poll_options || ["", ""]).length;
    return Array.from({ length: count }, (_, i) => existing[i] || paletteColor(i));
  });
  const [titleAlign, setTitleAlign] = useState(post.title_align === "center" ? "center" : "left");
  const [summaryAlign, setSummaryAlign] = useState(post.summary_align === "center" ? "center" : "left");
  const [topics, setTopics] = useState([]);
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    api.get("/topics").then((r) => setTopics(r.data.topics || []));
  }, []);

  const isPoll = post.type === "poll";
  const isLikert = isPoll && Array.isArray(post.likert_statements) && post.likert_statements.length > 0;

  const setOpt = (i, v) => {
    const next = [...options];
    next[i] = v;
    setOptions(next);
  };

  const setColor = (i, c) => {
    setOptionColors((prev) => {
      const next = [...prev];
      while (next.length <= i) next.push(paletteColor(next.length));
      next[i] = c;
      return next;
    });
  };

  const addOption = () => {
    if (options.length >= 6) return;
    setOptions([...options, ""]);
    setOptionColors((prev) => [...prev, paletteColor(prev.length)]);
  };

  const removeOption = (i) => {
    if (options.length <= 2) return;
    setOptions(options.filter((_, idx) => idx !== i));
    setOptionColors((prev) => prev.filter((_, idx) => idx !== i));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { title, topic, summary, title_align: titleAlign, summary_align: summaryAlign };
      if (isLikert) {
        // Likert polls: no body, no poll_options — only metadata edits
      } else if (isPoll) {
        const kept = options.map((opt, i) => ({ opt, color: optionColors[i] || paletteColor(i) }))
          .filter(({ opt }) => opt && opt.trim());
        payload.poll_options = kept.map((k) => k.opt.trim());
        payload.chart_colors = kept.map((k) => k.color);
        payload.chart_type = chartType;
      } else {
        payload.body = body;
      }
      await api.put(`/admin/posts/${post.id}`, payload);
      toast.success("Piece updated.");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-10 px-4" onClick={onClose}>
      <div
        className="bg-white border border-black w-full max-w-3xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-post-modal"
      >
        <div className="flex items-center justify-between border-b border-black px-6 py-4">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-700">Editor's Cut</div>
            <h3 className="serif text-2xl mt-1">Edit {post.type}</h3>
          </div>
          <button onClick={onClose} data-testid="edit-close" className="mono text-xs uppercase tracking-[0.2em] text-neutral-600 hover:text-black">
            Close
          </button>
        </div>
        <form onSubmit={save} className="p-6 space-y-6">
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Title (line breaks allowed)</label>
            <textarea
              data-testid="edit-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              rows={2}
              className="mt-2 w-full serif text-2xl border-b border-neutral-400 focus:border-black bg-transparent py-2 focus:outline-none resize-y leading-tight"
            />
            <AlignmentToggle value={titleAlign} onChange={setTitleAlign} testidPrefix="edit-title-align" />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Standfirst</label>
            <textarea
              data-testid="edit-summary-input"
              value={summary}
              onChange={(e) => setSummary(e.target.value.slice(0, 300))}
              rows={2}
              className="mt-2 w-full border border-neutral-300 focus:border-black bg-transparent p-3 focus:outline-none resize-none"
            />
            <AlignmentToggle value={summaryAlign} onChange={setSummaryAlign} testidPrefix="edit-summary-align" />
          </div>
          {isLikert ? (
            <div>
              <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Statements (read only)</label>
              <ol className="mt-2 space-y-2 list-decimal list-inside serif text-base text-neutral-700">
                {(post.likert_statements || []).map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ol>
              <p className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-3">
                Likert statements can&rsquo;t be edited here to avoid orphaning existing ratings. You can edit title, topic and standfirst.
              </p>
            </div>
          ) : isPoll ? (
            <div>
              <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Options &amp; colours</label>
              <div className="mt-2 space-y-4">
                {options.map((opt, i) => (
                  <div key={i} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        data-testid={`edit-option-${i}`}
                        value={opt}
                        onChange={(e) => setOpt(i, e.target.value)}
                        className="flex-1 border border-neutral-300 focus:border-black bg-transparent px-3 py-2 focus:outline-none"
                      />
                      <span
                        className="w-7 h-7 border border-neutral-300 shrink-0"
                        style={{ background: optionColors[i] || paletteColor(i) }}
                        title="Selected colour"
                        aria-hidden
                      />
                      {options.length > 2 && (
                        <button
                          type="button"
                          data-testid={`edit-option-remove-${i}`}
                          onClick={() => removeOption(i)}
                          className="mono text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {POLL_COLOR_PALETTE.map((c) => {
                        const active = (optionColors[i] || paletteColor(i)) === c;
                        return (
                          <button
                            type="button"
                            key={c}
                            onClick={() => setColor(i, c)}
                            data-testid={`edit-poll-color-${i}-${c}`}
                            aria-label={`Set option ${i + 1} colour ${c}`}
                            className={`w-6 h-6 border-2 ${active ? "border-black" : "border-transparent"}`}
                            style={{ background: c }}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
                {options.length < 6 && (
                  <button
                    type="button"
                    data-testid="edit-option-add"
                    onClick={addOption}
                    className="mono text-xs uppercase tracking-[0.2em] border border-neutral-300 px-3 py-1 hover:border-black"
                  >
                    + Add option
                  </button>
                )}
              </div>
              <div className="mt-6">
                <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Chart type</label>
                <div className="mt-2 flex gap-2 flex-wrap" data-testid="edit-chart-type">
                  {[
                    { key: "bar", label: "Bar" },
                    { key: "pie", label: "Pie" },
                    { key: "line", label: "Line" },
                  ].map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      data-testid={`edit-chart-type-${c.key}`}
                      onClick={() => setChartType(c.key)}
                      className={`mono text-[10px] uppercase tracking-[0.2em] px-3 py-1 border transition-colors ${
                        chartType === c.key
                          ? "bg-black text-white border-black"
                          : "border-neutral-300 hover:border-black"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Body</label>
              <textarea
                ref={bodyRef}
                data-testid="edit-body-input"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={12}
                className="mt-2 w-full border border-neutral-300 focus:border-black bg-transparent p-3 focus:outline-none serif text-base leading-relaxed resize-y"
              />
              <MarkdownToolbar textareaRef={bodyRef} value={body} onChange={setBody} />
            </div>
          )}
          <div className="flex justify-end gap-3 border-t border-neutral-200 pt-4">
            <button type="button" onClick={onClose} className="mono text-xs uppercase tracking-[0.2em] px-5 py-2 border border-neutral-300 hover:border-black">
              Cancel
            </button>
            <button type="submit" data-testid="edit-save-button" disabled={saving} className="bg-black text-white px-6 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-amber-700 disabled:opacity-40">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
